import {
  SetStateAction,
  Dispatch,
  ChangeEvent,
  useEffect,
  useState,
} from 'react';
import {
  DevHubAutocomplete,
  DevHubAutocompleteChangeReason,
  DevHubTextField,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { FunctionalArea } from '../insights/types/InsightsTypes';
import useDebounce from 'react-use/lib/useDebounce';

const FunctionalAreaQuery = gql`
  query Query($filter: FunctionalAreaListFilter) {
    devhub {
      connections {
        functionalAreas(filter: $filter) {
          id
          name
        }
      }
    }
  }
`;

interface Props {
  setFunctionalArea: Dispatch<SetStateAction<FunctionalArea>>;
  selectedFunctionalArea?: FunctionalArea;
  noMargin: boolean;
}

/**
 * Search component to grab functional areas from fusion
 * @param props props of the component
 * @param props.setFunctionalArea function to set the selected functional area
 * @param props.selectedFunctionalArea the selected functional area
 * @param props.noMargin to enable margin on the component
 */
function FunctionalAreaSearch(props: Props) {
  const [functionalAreas, setFunctionalAreas] = useState(
    [] as FunctionalArea[],
  );
  const [inputValue, setInputValue] = useState<string>('');
  const [funcArea, setFuncArea] = useState<string | undefined>(undefined);
  const [fetchFuncAreas, { loading, error }] = useLazyQuery(
    FunctionalAreaQuery,
    {
      errorPolicy: 'all',
      variables: { filter: { name: `${funcArea}*` } },
    },
  );

  useEffect(() => {
    if (funcArea) {
      fetchFuncAreas().then(results => {
        if (results?.data) {
          setFunctionalAreas(
            results.data?.devhub?.connections?.functionalAreas || [],
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [funcArea]);

  // Event Handler for the text box and to set the value
  const onHandleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setInputValue(value);
  };

  useDebounce(
    () => {
      setFuncArea(inputValue);
    },
    500,
    [inputValue],
  );

  // Event Handler to set selected party from a drop-down
  const onSelect = (
    _: Object,
    value: FunctionalArea | null,
    reason: DevHubAutocompleteChangeReason,
  ): void => {
    if (reason === 'clear') {
      props.setFunctionalArea({ name: 'clear' } as FunctionalArea);
    }
    if (!value) {
      return;
    }
    props.setFunctionalArea(value);
  };

  return (
    <>
      {error ? (
        <DevHubTextField
          error
          label="Error"
          size="small"
          noMargin={props.noMargin}
          fullWidth
          defaultValue="No Functional Areas Available"
          helperText="Failed to fetch functional area details."
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          size="small"
          fullWidth
          loading={loading}
          loadingText="Loading.."
          options={functionalAreas}
          onChange={onSelect}
          defaultValue={
            props?.selectedFunctionalArea?.name
              ? props?.selectedFunctionalArea
              : undefined
          }
          getOptionLabel={option => option?.name}
          renderInput={params => (
            <DevHubTextField
              {...params}
              label="Search for a Functional Area"
              variant="outlined"
              noMargin={props.noMargin}
              onChange={onHandleChange}
              required
            />
          )}
        />
      )}
    </>
  );
}

export default FunctionalAreaSearch;
